import { CHANNELS, type Approval, type Channel, type Item, type ItemStatus } from "@stratcms/sdk";
import { api, explain, isApprover, postJson, session } from "./api";
import { append, clear, fmtRelative, fmtWhen, h, icon, toast } from "./dom";
import { renderItem } from "./item";
import { CHANNEL_LABEL, STATUS_LABEL, channelBadge, statusPill } from "./labels";
import { openNewItem } from "./new-item";

const COLUMNS: { key: string; title: string; statuses: ItemStatus[]; hint: string }[] = [
  { key: "drafts", title: "Drafts", statuses: ["draft"], hint: "Being written" },
  { key: "review", title: "In review", statuses: ["in_review"], hint: "Waiting for an approver" },
  { key: "attention", title: "Needs attention", statuses: ["changes_requested", "failed"], hint: "Denied or failed" },
  { key: "approved", title: "Approved", statuses: ["approved"], hint: "Ready to schedule" },
  { key: "scheduled", title: "Scheduled", statuses: ["scheduled", "publishing"], hint: "Going out" },
  { key: "published", title: "Published", statuses: ["published"], hint: "Live" },
];

/* ------------------------------------------------------------------ state */

interface Filters {
  channel: Channel | "all";
  q: string;
  status: ItemStatus | "all";
  showArchived: boolean;
}
const filters: Filters = { channel: "all", q: "", status: "all", showArchived: false };
let items: Item[] = [];
let pending: Approval[] = [];

async function loadItems() {
  const r = await api.list({ channel: filters.channel === "all" ? undefined : filters.channel, q: filters.q || undefined, status: filters.status === "all" ? undefined : filters.status, limit: 200 });
  items = r.items;
  pending = (await api.approvals("pending")).items;
}

/* ------------------------------------------------------------------ shell */

const root = () => document.getElementById("app")!;

function navLink(href: string, label: string, ic: string, badge?: number) {
  const active = location.hash.startsWith(href) || (href === "#/board" && (location.hash === "" || location.hash === "#/"));
  return h("a", { href, class: `nav-link${active ? " active" : ""}`, "aria-current": active ? "page" : null }, icon(ic), h("span", {}, label), badge ? h("b", { class: "badge", "aria-label": `${badge} waiting` }, String(badge)) : null);
}

function shell(main: Node, opts: { title: string; toolbar?: Node | null }) {
  const me = session.me!;
  const rail = h(
    "aside",
    { class: "rail" },
    h("a", { class: "brand", href: "#/board" }, h("img", { src: "/img/strat-cms-icon.png", alt: "", width: 34, height: 34 }), h("span", { class: "brand-word" }, h("b", {}, "STRAT CMS"), h("small", {}, "STRAT EDGE SOLUTIONS"))),
    h(
      "nav",
      { class: "nav", "aria-label": "Main" },
      navLink("#/board", "Board", "board"),
      navLink("#/list", "All content", "list"),
      navLink("#/approvals", "Approvals", "checkCircle", pending.length),
      navLink("#/published", "Published", "globe"),
    ),
    h(
      "div",
      { class: "me" },
      h("span", { class: "avatar", "aria-hidden": "true" }, me.name.slice(0, 1).toUpperCase()),
      h("div", { class: "me-text" }, h("b", {}, me.name), h("small", {}, me.role ? me.role[0]!.toUpperCase() + me.role.slice(1) : me.kind)),
      h("button", { class: "icon-btn", title: "Sign out", "aria-label": "Sign out", onclick: signOut, "data-testid": "sign-out" }, icon("logout")),
    ),
  );
  const top = h(
    "header",
    { class: "topbar" },
    h("h1", { class: "page-title" }, opts.title),
    opts.toolbar ?? null,
    me.scopes.includes("content:write") ? h("button", { class: "btn btn-primary", onclick: () => openNewItem(), "data-testid": "new-item", "aria-label": "New content" }, icon("plus"), h("span", {}, "New")) : null,
    h("button", { class: "icon-btn only-phone", title: `Sign out ${me.name}`, "aria-label": "Sign out", onclick: signOut, "data-testid": "sign-out-phone" }, icon("logout")),
  );
  const tabbar = h(
    "nav",
    { class: "tabbar", "aria-label": "Main" },
    navLink("#/board", "Board", "board"),
    navLink("#/list", "List", "list"),
    navLink("#/approvals", "Approvals", "checkCircle", pending.length),
    navLink("#/published", "Live", "globe"),
  );
  const r = root();
  clear(r);
  append(r, [h("div", { class: "layout" }, rail, h("div", { class: "workspace" }, top, h("main", { id: "main", class: "main" }, main)), tabbar)]);
}

function filterBar(withStatus: boolean) {
  const chips = h(
    "div",
    { class: "chips", role: "group", "aria-label": "Channel" },
    ...(["all", ...CHANNELS] as const).map((c) =>
      h(
        "button",
        {
          class: `chip${filters.channel === c ? " on" : ""}`,
          "aria-pressed": filters.channel === c ? "true" : "false",
          onclick: () => {
            filters.channel = c;
            void route();
          },
        },
        c === "all" ? "All" : CHANNEL_LABEL[c],
      ),
    ),
  );
  const search = h("input", { type: "search", class: "search", placeholder: "Search titles and slugs", value: filters.q, "aria-label": "Search" });
  let t: ReturnType<typeof setTimeout>;
  search.addEventListener("input", () => {
    clearTimeout(t);
    t = setTimeout(() => {
      filters.q = search.value.trim();
      void route().then(() => {
        const s = document.querySelector<HTMLInputElement>("input.search");
        if (s) {
          s.focus();
          s.setSelectionRange(s.value.length, s.value.length);
        }
      });
    }, 250);
  });
  const status = withStatus
    ? h(
        "select",
        {
          class: "select",
          "aria-label": "Status",
          onchange: (e: Event) => {
            filters.status = (e.target as HTMLSelectElement).value as ItemStatus | "all";
            void route();
          },
        },
        h("option", { value: "all" }, "Every status"),
        ...Object.entries(STATUS_LABEL).map(([k, v]) => h("option", { value: k, selected: filters.status === k }, v)),
      )
    : null;
  return h("div", { class: "filters" }, h("label", { class: "search-wrap" }, icon("search", "ic ic-sm"), search), chips, status);
}

function card(i: Item) {
  return h(
    "a",
    { class: `card-item st-${i.status}`, href: `#/item/${i.id}`, "data-testid": `card-${i.slug}` },
    h("div", { class: "card-top" }, channelBadge(i.channel), h("span", { class: "ver" }, `V${i.latestVersionNumber}`)),
    h("h3", { class: "card-title" }, i.title),
    h("p", { class: "card-meta" }, i.status === "changes_requested" || i.status === "failed" ? statusPill(i.status) : null, h("span", {}, fmtRelative(i.updatedAt))),
  );
}

function boardView() {
  const visible = items.filter((i) => filters.showArchived || i.status !== "archived");
  const cols = COLUMNS.map((c) => {
    const list = visible.filter((i) => c.statuses.includes(i.status));
    return h(
      "section",
      { class: `col col-${c.key}${list.length ? "" : " is-empty"}`, "aria-label": c.title },
      h("header", { class: "col-head" }, h("h2", {}, c.title), h("span", { class: "count" }, String(list.length))),
      h("p", { class: "col-hint" }, c.hint),
      h("div", { class: "col-body" }, list.length ? list.map(card) : h("p", { class: "empty" }, "Nothing here")),
    );
  });
  shell(h("div", { class: "board-wrap" }, filterBar(false), h("div", { class: "board" }, ...cols)), { title: "Content board" });
}

function table(list: Item[]) {
  if (!list.length) return h("div", { class: "empty-state" }, h("p", {}, "No content matches these filters."));
  return h(
    "div",
    { class: "table-wrap" },
    h(
      "table",
      { class: "table" },
      h("thead", {}, h("tr", {}, h("th", {}, "Title"), h("th", {}, "Channel"), h("th", {}, "Status"), h("th", {}, "Version"), h("th", {}, "Updated"))),
      h(
        "tbody",
        {},
        ...list.map((i) =>
          h(
            "tr",
            {},
            h("td", { class: "t-title" }, h("a", { href: `#/item/${i.id}` }, i.title), h("small", {}, i.slug)),
            h("td", {}, channelBadge(i.channel)),
            h("td", {}, statusPill(i.status)),
            h("td", {}, `V${i.latestVersionNumber}`),
            h("td", { class: "t-when" }, fmtWhen(i.updatedAt)),
          ),
        ),
      ),
    ),
  );
}

function listView() {
  shell(h("div", {}, filterBar(true), table(items)), { title: "All content" });
}

function publishedView() {
  shell(h("div", {}, filterBar(false), table(items.filter((i) => i.publishedVersionId))), { title: "Published" });
}

function approvalsView() {
  const byItem = new Map(items.map((i) => [i.id, i]));
  const body = pending.length
    ? h(
        "ul",
        { class: "approval-list" },
        ...pending.map((a) => {
          const it = byItem.get(a.itemId);
          return h(
            "li",
            {},
            h(
              "a",
              { href: `#/item/${a.itemId}`, class: "approval-row" },
              it ? channelBadge(it.channel, false) : null,
              h("div", {}, h("b", {}, it?.title ?? a.itemId), h("small", {}, `V${a.versionNumber} · asked by ${a.requestedByName} · ${fmtRelative(a.requestedAt)}`)),
              h("span", { class: "btn btn-ghost" }, isApprover() ? "Review" : "View"),
            ),
          );
        }),
      )
    : h("div", { class: "empty-state" }, icon("checkCircle", "ic ic-xl"), h("p", {}, "Nothing is waiting for approval."));
  shell(body, { title: "Waiting for approval" });
}

/* ------------------------------------------------------------------ sign-in */

function loginView() {
  const r = root();
  clear(r);
  const email = h("input", { type: "email", required: true, placeholder: "you@strategyedge.co.za", autocomplete: "email", "aria-label": "Email", id: "dev-email" });
  const role = h("select", { class: "select", "aria-label": "Role", id: "dev-role" }, ...["editor", "approver", "admin", "viewer"].map((x) => h("option", { value: x }, x[0]!.toUpperCase() + x.slice(1))));
  const dev = session.devLogin
    ? h(
        "form",
        {
          class: "dev-login",
          onsubmit: async (e: Event) => {
            e.preventDefault();
            const res = await postJson("/auth/dev-login", { email: email.value, role: role.value, name: email.value.split("@")[0] });
            if (res.ok) {
              await boot();
            } else {
              const body = (await res.json().catch(() => null)) as { error?: { message?: string } } | null;
              toast(body?.error?.message ?? "Local sign-in failed.", "error");
            }
          },
        },
        h("p", { class: "fine" }, "Local development only. Not available on the live CMS."),
        email,
        role,
        h("button", { class: "btn btn-ghost btn-block", type: "submit" }, "Sign in locally"),
      )
    : null;
  append(r, [
    h(
      "div",
      { class: "login" },
      h(
        "div",
        { class: "login-card" },
        h("img", { src: "/img/strat-cms-icon.png", alt: "", width: 64, height: 64 }),
        h("h1", {}, "Strat CMS"),
        h("p", { class: "muted" }, "One place for every Strat Edge post: blog, LinkedIn, Instagram and YouTube."),
        h("a", { class: "btn btn-primary btn-block btn-lg", href: `/auth/login?return_to=${encodeURIComponent(location.hash ? `/${location.hash}` : "/")}` }, icon("shield"), h("span", {}, "Sign in with Strat Edge ID")),
        dev,
      ),
    ),
  ]);
}

async function signOut() {
  await postJson("/auth/logout", {});
  session.me = null;
  loginView();
}

/* ------------------------------------------------------------------ router */

let routeSeq = 0;

/** Renders the current hash. A newer navigation always wins: stale renders are dropped. */
export async function route() {
  const my = ++routeSeq;
  const current = () => my === routeSeq;
  const guardedShell: typeof shell = (main, opts) => {
    if (current()) shell(main, opts);
  };
  const hash = location.hash || "#/board";
  try {
    const m = /^#\/item\/(cnt_[A-Za-z0-9]+)(?:\/(\w+))?/.exec(hash);
    if (m) {
      pending = (await api.approvals("pending")).items;
      guardedShell(h("div", { class: "loading" }, "Loading..."), { title: "Content" });
      await renderItem(m[1]!, (m[2] as "edit" | "preview" | "review" | "history" | undefined) ?? undefined, guardedShell, current);
      return;
    }
    await loadItems();
    if (!current()) return;
    if (hash.startsWith("#/list")) listView();
    else if (hash.startsWith("#/approvals")) approvalsView();
    else if (hash.startsWith("#/published")) publishedView();
    else boardView();
  } catch (e) {
    if (!current()) return;
    if ((e as { code?: string }).code === "UNAUTHENTICATED") return loginView();
    toast(explain(e), "error");
  }
}

async function boot() {
  try {
    const health = (await (await fetch("/api/v1/public/health")).json()) as { devLogin?: boolean };
    session.devLogin = !!health.devLogin;
    const s = (await (await fetch("/auth/session", { credentials: "same-origin" })).json()) as { signedIn: boolean };
    session.me = s.signedIn ? (await api.me()).principal : null;
  } catch {
    session.me = null;
  }
  if (!session.me) return loginView();
  await route();
}

window.addEventListener("hashchange", () => void route());
void boot();
